import { BaseInput, FlexCol, FlexRow } from '@/src/components'
import { BaseText } from '@/src/components/BaseText'
import { matchesSearchQuery } from '@/src/components/SearchBox/search.utils'
import { BaseButton } from '@/src/components/buttons/BaseButton'
import { useLocationsContext } from '@/src/contexts/LocationsContext'
import { appColors } from '@/src/theme'
import { Box, Divider, Flex, IconButton, Text, useDisclosure } from '@chakra-ui/react'
import { SimpleConfirmModal } from '@components'
import { type FC, useState } from 'react'
import { PlusCircle, RotateCcw, Search, X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { WIDGETS_SVGS, WIDGET_IDS, useDashboardEditingStore } from '../../infrastructure/useDashboardEditingStore'
import { SelectDeviceModal } from './SelectDeviceModal'
import { SelectEnpiModal } from './SelectEnpiModal'

export const WidgetsSideBar: FC = () => {
  const { t } = useTranslation()
  const { toggleIsInEditMode, addToTmpLayout, getAvailableWidgets, restoreDefaultLayout } = useDashboardEditingStore()
  const locations = useLocationsContext()
  const userCanSeeAllLocations = locations.userCanSeeAllLocations
  const [searchText, setSearchText] = useState<string>('')
  const cancelModalDisclosure = useDisclosure()
  const resetModalDisclosure = useDisclosure()
  const selectDeviceModalDisclosure = useDisclosure()
  const selectEnpiModalDisclosure = useDisclosure()

  const widgets = getAvailableWidgets()

  const onAccept = () => {
    toggleIsInEditMode()
  }

  const onCancel = () => {
    cancelModalDisclosure.onOpen()
  }

  const onModalCancel = () => {
    cancelModalDisclosure.onClose()
  }

  const onModalAccept = () => {
    window.location.reload()
  }

  return (
    <FlexCol bg='white' borderLeftWidth={'1px'} width={'300px'} paddingY={'24px'} paddingX='4px' height={'100%'}>
      <FlexRow mx={4} alignItems={'center'} justifyContent={'space-between'}>
        <BaseText variant='title'>{t('dashboard.widgets_sidebar_title')}</BaseText>
        <IconButton
          aria-label='Close'
          size='sm'
          onClick={onCancel}
          padding={0}
          width={'fit-content'}
          height={'fit-content'}
          borderWidth={0}
          _focus={{ borderWidth: 0 }}
        >
          <X />
        </IconButton>
      </FlexRow>
      <Divider mt={'4px'} mb={'16px'}></Divider>
      <Box mx={4}>
        <BaseInput
          onKeyDown={e => {
            if (e.nativeEvent.key === 'Enter') {
            }
          }}
          onChange={e => {
            setSearchText(e.target.value)
          }}
          value={searchText}
          fontSize={'14px'}
          placeholder={t('dashboard.search_widget')}
          IconRight={<Search size='16px' color='#B4B4B4' />}
          borderColor={appColors.border}
          height={'40px'}
        />
      </Box>

      <FlexCol mt={4} gap={'8px'} height={'100%'} overflowY='auto' className='innerScrollBar' alignItems='center'>
        {widgets
          .filter(item => matchesSearchQuery(searchText, item.label))
          .map(item => {
            if (!userCanSeeAllLocations && item.id === WIDGET_IDS.BUDGET_AND_COST) return
            const isDepleted = item.instanceCount === item.maxInstances
            return (
              <Flex
                key={item.id}
                bgImage={`url(${WIDGETS_SVGS[item.id]})`}
                bgSize='100% 100%'
                bgPosition='center'
                bgRepeat='no-repeat'
                bgColor='white'
                w='256px'
                h='150px'
                minHeight='150px'
                borderRadius={'4px'}
                position='relative'
                overflow='hidden'
              >
                <Flex
                  position='absolute'
                  insetX={0}
                  bottom='8px'
                  alignItems='center'
                  justifyContent='center'
                  px='20px'
                  zIndex={1}
                  pointerEvents='none'
                >
                  <Text
                    textAlign='center'
                    color={appColors.foreground}
                    fontSize='13px'
                    fontWeight={400}
                    lineHeight='16px'
                    maxWidth='200px'
                    whiteSpace='normal'
                  >
                    {item.label}
                  </Text>
                </Flex>
                <Flex
                  position='absolute'
                  inset={0}
                  justifyContent={'center'}
                  alignItems={'center'}
                  borderRadius='4px'
                  cursor={isDepleted ? 'not-allowed' : 'pointer'}
                  w='100%'
                  h='100%'
                  zIndex={2}
                  backgroundColor='transparent'
                  boxShadow='inset 0 0 0 1px transparent'
                  transition='background-color 0.15s ease, box-shadow 0.15s ease'
                  sx={{
                    '& svg': {
                      opacity: 0,
                      transition: 'opacity 0.15s ease',
                    },
                    '&:hover svg': {
                      opacity: isDepleted ? 0 : 1,
                    },
                  }}
                  _hover={{
                    backgroundColor: isDepleted ? 'transparent' : 'rgba(238,250,243,0.3)',
                    boxShadow: isDepleted ? 'inset 0 0 0 1px transparent' : `inset 0 0 0 1px ${appColors.accentPrimary}`,
                  }}
                  onClick={() => {
                    if (item.instanceCount < item.maxInstances) {
                      if (item.id === WIDGET_IDS.LOCATION_AND_DEVICE) {
                        selectDeviceModalDisclosure.onOpen()
                      } else if (item.id === WIDGET_IDS.ENPI) {
                        selectEnpiModalDisclosure.onOpen()
                      } else {
                        addToTmpLayout(item.id)
                      }
                    }
                  }}
                >
                  <PlusCircle color={appColors.accentPrimary} />
                </Flex>
              </Flex>
            )
          })}
      </FlexCol>
      <FlexRow mt={'24px'} mx={4} justifyContent={'space-between'}>
        <BaseButton variant='secondary' text={t('common.cancel')} onClick={onCancel} />
        <BaseButton onClick={onAccept} text={t('common.save')} />
      </FlexRow>
      <BaseButton
        variant='tertiary'
        borderWidth={0}
        mt={'16px'}
        borderRadius={'8px'}
        paddingTop={0}
        paddingBottom={0}
        onClick={resetModalDisclosure.onOpen}
      >
        <FlexRow width={'100%'} justifyContent={'center'} gap={'12px'} alignItems={'center'}>
          <RotateCcw size={'16px'} />
          <Text fontSize={'12px'} lineHeight={'18px'} letterSpacing={'0.24px'}>
            {t('dashboard.reset_cockpit')}
          </Text>
        </FlexRow>
      </BaseButton>

      <SimpleConfirmModal
        acceptLabel={t('common.discard_changes')}
        cancelLabel={t('common.cancel')}
        bodyText={t('dashboard.discard_changes_confirm')}
        title={t('dashboard.discard')}
        isOpen={cancelModalDisclosure.isOpen}
        onClose={onModalCancel}
        onAccept={onModalAccept}
      />
      <SimpleConfirmModal
        acceptLabel={t('common.reset')}
        cancelLabel={t('common.cancel')}
        bodyText={t('dashboard.reset_layout_confirm')}
        title={t('dashboard.reset_layout')}
        isOpen={resetModalDisclosure.isOpen}
        onClose={resetModalDisclosure.onClose}
        onAccept={() => {
          restoreDefaultLayout().then(() => window.location.reload())
        }}
      />
      <SelectDeviceModal isOpen={selectDeviceModalDisclosure.isOpen} onClose={selectDeviceModalDisclosure.onClose} />
      <SelectEnpiModal
        isOpen={selectEnpiModalDisclosure.isOpen}
        onClose={selectEnpiModalDisclosure.onClose}
        onConfirm={enpiIds => addToTmpLayout(WIDGET_IDS.ENPI, { enpiIds })}
        actionButtonText={t('dashboard.insert_widget')}
      />
    </FlexCol>
  )
}
